/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';

/**
 * Interface defining the return values of the useUserEditorLoadingStatus custom hook.
 */
interface useUserEditorLoadingStatusProps {
  isApplicationsLoading: boolean;
  setIsApplicationsLoading: (loading: boolean) => void;
  isRolesLoading: boolean;
  setIsRolesLoading: (loading: boolean) => void;
}

/**
 * Custom hook to track loading states of Applications and Roles components
 * within the UserEditor. This hook is specific to this editor and resides
 * in the same directory as `useEditor`.
 *
 * It allows the parent component to determine when it is safe to submit the form
 * by exposing setters that are passed to the child components.
 *
 * Example:
 *
 * ```typescript
 * 
 *    import { useUserEditorLoadingStatus } from './useUserEditorLoadingStatus';
 * 
 *    const { 
 *      isApplicationsLoading, 
 *      setIsApplicationsLoading, 
 *      isRolesLoading, 
 *      setIsRolesLoading 
 *    } = useUserEditorLoadingStatus(includeApplications);
 *
 *    <Applications onLoadingChange={setIsApplicationsLoading} />
 *    <RoleChooser onLoadingChange={setIsRolesLoading} />
 *    <PrimaryButton disabled={isApplicationsLoading || isRolesLoading}>
 *      Done
 *    </PrimaryButton>
 *
 * ```
 *
 * @param isApplicationsLoading Loading state of Applications.
 * @param setIsApplicationsLoading Setter for Applications loading.
 * @param isRolesLoading Loading state of Roles.
 * @param setIsRolesLoading Setter for Roles loading.
 * @returns An Object containing loading states and setters.
 */
export function useUserEditorLoadingStatus(
  includeApplications: boolean
): useUserEditorLoadingStatusProps {
  const [isApplicationsLoading, setIsApplicationsLoading] = useState<boolean>(includeApplications);
  const [isRolesLoading, setIsRolesLoading]               = useState<boolean>(!includeApplications);

  return {
    isApplicationsLoading,
    setIsApplicationsLoading,
    isRolesLoading,
    setIsRolesLoading
  };
}
